(ns shared.core.components.recharts.legend
  (:require [shared.core.style :as style]
            [shared.core.reactive :as re]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.util :as util]))

(defn legend-item
  "Single legend item showing category name and percentage. Same chip as every other
   chart legend (see `recharts.util/legend-item-styles`), with the percentage pushed to
   its trailing edge."
  [{:keys [name percentage fill]}]
  (let [{:keys [chip swatch text]} (recharts.util/legend-item-styles)]
    [:div {:key name
           :style (merge chip {:justify-content :space-between
                               :gap 12
                               :min-width 0
                               :max-width "100%"})}
     [:div {:style {:display :flex
                    :align-items :center
                    :gap (:gap chip)
                    :min-width 0}}
      [:div {:style (assoc swatch :background-color fill)}]
      [:span {:style text}
       name]]
     [:span {:style (merge text {:font-weight style/font-medium-weight
                                 :flex "0 0 auto"})}
      (str (.toFixed percentage 1) "%")]]))

(defn legend
  "Legend component showing all categories with percentages. Lays items out in
   columns; `wrap?` instead flows them along a single wrapping row, for a legend
   sitting under its chart rather than beside it."
  [legend-data & {:keys [column-max-count container-style wrap?]}]
  (if wrap?
    (->> legend-data
         (mapv (fn [item] [legend-item item]))
         (into [:div {:style (merge {:display :flex
                                     :flex-wrap :wrap
                                     :justify-content :center
                                     :align-items :center
                                     :gap 12}
                                    container-style)}]))
    (let [columns (if (some->> column-max-count (> (count legend-data)))
                    (partition-all column-max-count legend-data)
                    [legend-data])]
      (->> columns
           (mapv (fn [column-items]
                   (->> column-items
                        (mapv (fn [item]
                                [legend-item item]))
                        (into [:div {:style {:display :flex
                                             :flex-direction :column
                                             :gap 10}}]))))
           (into [:div {:style (merge {:display :flex
                                       :flex-direction :row
                                       :gap 25}
                                      container-style)}])))))

;; Once a side legend would squeeze the chart below this, the legend goes underneath it.
(def ^:private min-chart-size 200)
(def ^:private side-legend-max-width 400)
(def ^:private bottom-legend-gap 16)

(defn chart-with-legend
  "Lays a pie/donut chart out with its legend, sized to the container it is given: the
   legend sits beside the chart while there is room for both, and wraps underneath it
   when there isn't. `chart-fn` is called with the px size the chart should draw at —
   `max-size` where the container allows, smaller where it doesn't."
  [_ _]
  (let [container-size* (re/ratom nil)
        container-ref (util/size-ref container-size*)]
    (fn [{:keys [legend-data show-legend? max-size legend-gap column-max-count]
          :or {max-size 500
               legend-gap 50}}
         chart-fn]
      (let [legend-width (when show-legend?
                           (min side-legend-max-width (recharts.util/fit-legend-width legend-data)))
            container-width (:width @container-size*)
            side? (and show-legend?
                       (or (nil? container-width)
                           (>= (- container-width legend-width legend-gap) min-chart-size)))
            available (cond
                        (nil? container-width) max-size
                        side? (- container-width legend-width legend-gap)
                        :else container-width)
            legend-cmp (when show-legend?
                         (if side?
                           [legend legend-data
                            :column-max-count column-max-count
                            :container-style {:max-width side-legend-max-width}]
                           [legend legend-data
                            :wrap? true
                            :container-style {:max-width "100%"}]))
            chart (chart-fn (js/Math.floor (max min-chart-size (min max-size available))))]
        [:div {:ref container-ref
               :style {:display :flex
                       :flex-direction (if side? :row :column)
                       :align-items :center
                       :justify-content :center
                       :gap (if side? legend-gap bottom-legend-gap)
                       :width "100%"}}
         (if side?
           [:<> legend-cmp chart]
           [:<> chart legend-cmp])]))))
